import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  FiHome, FiUser, FiDollarSign, FiFileText, FiEdit3,
  FiSave, FiX, FiPlus, FiTrash2, FiFolder, FiWifi,
  FiLock, FiMail, FiPhone, FiCalendar, FiExternalLink,
  FiZap, FiDroplet, FiWind, FiTrash, FiTv, FiChevronDown,
  FiChevronUp, FiImage, FiArrowLeft, FiPaperclip,
  FiUsers, FiMessageSquare, FiClock, FiInfo, FiKey,
  FiEye, FiList, FiBarChart2, FiTrendingUp
} from 'react-icons/fi';
import { propertiesService, Property } from '../services/properties.service';
import { documentsService, Document } from '../services/documents.service';
import { expensesService, Expense } from '../services/expenses.service';
import * as ps from '../services/pocketsmith.service';
import { toast } from 'react-hot-toast';
import { subDays, startOfMonth } from 'date-fns';
import { safeFormatDate } from '../utils/dateUtils';
import { ImageGalleryInline } from '../components/ImageGalleryInline';
import { DocumentViewer } from '../components/DocumentViewer';
import { tw, monoFont, statusColors, getCategoryStyle } from '../utils/terminalTheme';
import { api } from '../config/api';

interface ExtendedProperty extends Property {
  // Additional fields not in base Property
  lot_size?: string;
  year_purchased?: number;
  purchase_amount?: number;
  current_value?: number;
  annual_property_taxes?: number;

  // HOA Information
  hoa_name?: string;
  hoa_phone?: string;
  hoa_email?: string;
  hoa_website?: string;
  hoa_company_name?: string | null;
  hoa_account_number?: string | null;

  // Payment Schedule Information
  payment_bank_account?: string | null;
  payment_frequency?: string | null;
  payment_schedule_start_date?: string | null;
  next_payment_date?: string | null;
  payment_details?: string | null;

  // Neighbor Information
  neighbor_name?: string;
  neighbor_phone?: string;
  neighbor_email?: string;
  neighbor_notes?: string;

  // Access Information
  lockbox_code?: string;
  garage_code?: string;
  wifi_network?: string;
  wifi_password?: string;

// Utility Accounts
  cox_account?: string | null;
  cox_monthly?: number | null;
  nv_energy_account?: string | null;
  nv_energy_monthly?: number | null;
  sw_gas_account?: string | null;
  sw_gas_monthly?: number | null;
  water_account?: string | null;
  republic_account?: string | null;
  water_monthly?: number | null;
  // Notes
  general_notes?: string;
  maintenance_notes?: string;
  tenant_notes?: string;

  // Current Tenant (already in base Property, removed duplicates)
  tenant_status?: string | null;
}

interface CollapsibleSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  defaultOpen?: boolean;
  iconColor?: string;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  icon,
  children,
  defaultOpen = false,
  iconColor = "text-cyan-400"
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-700/30 transition-colors"
      >
        <h3 className="text-sm font-mono font-medium text-white flex items-center gap-3 uppercase tracking-wider">
          <span className={iconColor}>{icon}</span>
          {title}
        </h3>
        {isOpen ? (
          <FiChevronUp className="w-4 h-4 text-gray-500" />
        ) : (
          <FiChevronDown className="w-4 h-4 text-gray-500" />
        )}
      </button>
      {isOpen && (
        <div className="px-6 pb-6 border-t border-gray-700/50">
          {children}
        </div>
      )}
    </div>
  );
};

const PropertyDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [property, setProperty] = useState<ExtendedProperty | null>(null);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [recentDocuments, setRecentDocuments] = useState<Document[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  const [imageDocuments, setImageDocuments] = useState<Document[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [recentExpenses, setRecentExpenses] = useState<Expense[]>([]);
  const [monthlyExpenses, setMonthlyExpenses] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditMode, setIsEditMode] = useState(false);
  const [cashFlow, setCashFlow] = useState<ps.PropertyCashFlow | null>(null);

  // Utility bill history
  interface UtilityBill {
    id: number;
    provider: string;
    account_number: string;
    billing_date: string;
    due_date: string | null;
    amount: number;
    usage_amount: number | null;
    usage_unit: string | null;
    meter_read_date: string | null;
    pdf_filename: string | null;
    notes: string | null;
  }
  const [utilityBills, setUtilityBills] = useState<{ data: UtilityBill[]; total_12mo: number; avg_monthly: number } | null>(null);

  // Rent payment history
  interface RentPayment {
    id: number;
    date: string;
    amount: number;
    payee: string;
    labels: string;
    account: string;
  }
  const [rachioZones, setRachioZones] = useState<any>(null);
  const [rentPayments, setRentPayments] = useState<{ data: RentPayment[]; total: number; avg_monthly: number; count: number } | null>(null);

  useEffect(() => {
    if (id) {
      loadPropertyData();
      loadRachioZones();
    }
  }, [id]);

  const loadRachioZones = async () => {
    try {
      const res = await api.get(`/api/v1/rachio/property/${id}/zones`);
      setRachioZones(res.data);
    } catch {
      // No irrigation at this property
    }
  };

  const loadPropertyData = async () => {
    try {
      setIsLoading(true);
      const propertyData = await propertiesService.getProperty(parseInt(id!));
      setProperty(propertyData);

      // Load documents for this property
      const docs = await documentsService.getDocuments({
        property_id: parseInt(id!),
        limit: 100
      });
      setDocuments(docs);

      // Filter and sort image documents
      const imageDocs = docs.filter(doc =>
        doc.mime_type?.startsWith('image/') ||
        /\.(jpg|jpeg|png|gif|bmp|tiff?)$/i.test(doc.filename)
      );
      const sortedImageDocs = [...imageDocs].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setImageDocuments(sortedImageDocs);

      // Get recent documents (last 10)
      const sortedDocs = [...docs].sort((a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      setRecentDocuments(sortedDocs.slice(0, 10));

      // Load expenses for this property
      const expensesData = await expensesService.getExpenses(parseInt(id!));
      setExpenses(expensesData);

      // Get recent expenses (last 10)
      const sortedExpenses = [...expensesData].sort((a, b) =>
        new Date(b.expense_date).getTime() - new Date(a.expense_date).getTime()
      );
      setRecentExpenses(sortedExpenses.slice(0, 10));

      // Calculate this month's expenses
      const thisMonthStart = startOfMonth(new Date());
      const thisMonthExpenses = expensesData.filter(expense =>
        new Date(expense.expense_date) >= thisMonthStart
      );
      const monthlyTotal = thisMonthExpenses.reduce((sum, expense) => sum + expense.amount, 0);
      setMonthlyExpenses(monthlyTotal);

      // Load PocketSmith cash flow data
      try {
        const cfData = await ps.getPropertyCashFlow(parseInt(id!), 12);
        if (cfData.success) setCashFlow(cfData);
      } catch (e) {
        // PocketSmith data optional - don't fail if not available
      }

      // Load utility bill history
      try {
        const ubRes = await api.get(`/properties/${id}/utility-bills`);
        if (ubRes.data?.success) setUtilityBills(ubRes.data);
      } catch (e) {
        // Utility bill data optional
      }

      // Load rent payment history
      try {
        const rpRes = await api.get(`/properties/${id}/rent-payments`);
        if (rpRes.data?.success) setRentPayments(rpRes.data);
      } catch (e) {
        // Rent payment data optional
      }

    } catch (error) {
      toast.error('Failed to load property details');
      console.error('Error loading property:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={tw.loadingContainer}>
        <div className={tw.loading}></div>
      </div>
    );
  }

  if (!property) {
    return <div className={tw.empty}>Property not found</div>;
  }

  const hasUtilityInfo = property.cox_account || property.nv_energy_account || property.sw_gas_account || property.water_account || property.republic_account;
  const hasHOAInfo = property.hoa_url || property.hoa_login_url || property.hoa_username || property.hoa_password || property.hoa_notes || property.hoa_monthly_fee || property.hoa_company_name || property.hoa_account_number || property.payment_bank_account || property.payment_frequency || property.payment_schedule_start_date || property.next_payment_date || property.payment_details;
  const hasFinancialInfo = property.purchase_amount || property.current_value || property.monthly_rent || property.annual_property_taxes;
  const hasNeighborInfo = property.neighbor_name || property.neighbor_phone || property.neighbor_email || property.neighbor_notes;
  const hasAccessInfo = property.lockbox_code || property.garage_code || property.wifi_network || property.wifi_password;
  const hasNotes = property.general_notes || property.maintenance_notes || property.tenant_notes;

  return (
    <div className={tw.page}>
      {/* Terminal Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
              <span className="text-cyan-400">~/</span>properties/<span className="text-white">{property.name || property.address}</span>
              <span className="text-gray-600 text-sm font-normal ml-3">
                <span className="text-green-500">$</span> {isEditMode ? 'edit --mode interactive' : 'cat details.conf'}
              </span>
            </h1>
            <p className="text-gray-500 text-sm font-mono mt-1">{property.address}</p>
          </div>
          <div className="flex gap-3">
            {!isEditMode ? (
              <>
                <button
                  onClick={() => navigate(`/properties/${id}/gallery`)}
                  className={tw.btnSecondary + ' flex items-center gap-2'}
                >
                  <FiImage className="w-4 h-4" />
                  Gallery ({documents.filter(d => d.mime_type?.startsWith('image/')).length})
                </button>
                <Link
                  to="/properties"
                  className={tw.btnSecondary + ' flex items-center gap-2 no-underline'}
                >
                  <FiArrowLeft className="w-4 h-4" />
                  Back
                </Link>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate(`/properties/${id}`)}
                  className={tw.btnSecondary + ' flex items-center gap-2'}
                >
                  <FiEye className="w-4 h-4" />
                  View Details
                </button>
                <Link
                  to="/properties"
                  className={tw.btnSecondary + ' flex items-center gap-2 no-underline'}
                >
                  <FiArrowLeft className="w-4 h-4" />
                  Back
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className={tw.statCard + ' text-center'}>
            <p className={tw.statLabel}>Current Tenants</p>
            <p className={tw.statValueCyan}>{property.tenant_name ? '1' : '0'}</p>
          </div>
          <div className={tw.statCard + ' text-center'}>
            <p className={tw.statLabel}>Email Forwarder</p>
            <p className="text-lg font-bold text-cyan-400 font-mono">
              {(property as any).email_prefix ? `${(property as any).email_prefix}@gpume.com` : 'Not set'}
            </p>
          </div>
          <div className={tw.statCard + ' text-center'}>
            <p className={tw.statLabel}>Media Files</p>
            <p className={tw.statValueCyan}>{documents.length}</p>
          </div>
          <div className={tw.statCard + ' text-center'}>
            <p className={tw.statLabel}>Monthly Rent</p>
            <p className="text-2xl font-bold font-mono text-green-500">${property.monthly_rent || '0'}</p>
          </div>
        </div>

        {/* Property Management Section */}
        <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-6 mb-8">
          <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <FiFolder className="w-4 h-4 text-cyan-400" />
            Property Management
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <button
              onClick={() => navigate(`/properties/${id}/documents`)}
              className="bg-blue-500/15 border border-blue-500/30 hover:bg-blue-500/25 text-white rounded-lg p-4 transition-colors font-mono"
            >
              <FiFileText className="w-6 h-6 mx-auto mb-2 text-blue-400" />
              <div className="font-medium text-blue-400 text-sm">Documents</div>
              <div className="text-xs text-gray-500 mt-1">View all documents, contracts, and files for this property.</div>
            </button>
            <button
              onClick={() => navigate(`/properties/${id}/expenses`)}
              className="bg-green-500/15 border border-green-500/30 hover:bg-green-500/25 text-white rounded-lg p-4 transition-colors font-mono"
            >
              <FiDollarSign className="w-6 h-6 mx-auto mb-2 text-green-400" />
              <div className="font-medium text-green-400 text-sm">Expenses</div>
              <div className="text-xs text-gray-500 mt-1">Track all expenses, repairs, and maintenance costs.</div>
            </button>
            <button
              onClick={() => navigate(`/properties/${id}/gallery`)}
              className="bg-purple-500/15 border border-purple-500/30 hover:bg-purple-500/25 text-white rounded-lg p-4 transition-colors font-mono"
            >
              <FiImage className="w-6 h-6 mx-auto mb-2 text-purple-400" />
              <div className="font-medium text-purple-400 text-sm">Gallery</div>
              <div className="text-xs text-gray-500 mt-1">View photos and visual documentation of the property.</div>
            </button>
            <button
              onClick={() => setIsEditMode(true)}
              className="bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 text-white rounded-lg p-4 transition-colors font-mono"
            >
              <FiEdit3 className="w-6 h-6 mx-auto mb-2 text-amber-400" />
              <div className="font-medium text-amber-400 text-sm">Edit Property</div>
              <div className="text-xs text-gray-500 mt-1">Edit basic property information and details.</div>
            </button>
          </div>
        </div>

        {/* Recent Activity Dashboard */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Recent Expenses */}
          <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-mono text-gray-500 uppercase tracking-wider flex items-center gap-2">
                <FiDollarSign className="w-4 h-4 text-green-400" />
                Recent Expenses
                {expenses.length > 0 && <span className="text-[10px] text-gray-600 font-normal lowercase">({recentExpenses.length} of {expenses.length})</span>}
              </h3>
              <Link
                to={`/properties/${id}/expenses`}
                className="text-sm text-cyan-400 hover:text-cyan-300 font-mono"
              >
                View All
              </Link>
            </div>

            <div className="mb-4 p-3 bg-gray-900/50 border border-gray-700/30 rounded-lg">
              <div className="text-xs text-gray-500 font-mono uppercase tracking-wider">This Month</div>
              <div className="text-2xl font-bold text-green-400 font-mono">${monthlyExpenses.toFixed(2)}</div>
            </div>

            {recentExpenses.length > 0 ? (
              <div className="space-y-2">
                {recentExpenses.map((expense) => (
                  <div key={expense.id} className="flex items-center justify-between p-3 bg-gray-900/30 border border-gray-700/30 rounded-lg">
                    <div>
                      <div className="text-white font-mono text-sm">{expense.vendor || 'Unknown Vendor'}</div>
                      <div className="text-xs text-gray-500 font-mono">
                        {safeFormatDate(expense.expense_date)}
                      </div>
                    </div>
                    <div className="text-green-400 font-mono text-sm font-medium">
                      ${expense.amount.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={tw.empty}>No recent expenses</p>
            )}
          </div>

          {/* Recent Documents */}
          <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xs font-mono text-gray-500 uppercase tracking-wider flex items-center gap-2">
                <FiFileText className="w-4 h-4 text-blue-400" />
                Recent Documents
                {documents.length > 0 && <span className="text-[10px] text-gray-600 font-normal lowercase">({recentDocuments.length} of {documents.length})</span>}
              </h3>
              <Link
                to={`/properties/${id}/documents`}
                className="text-sm text-cyan-400 hover:text-cyan-300 font-mono"
              >
                View All
              </Link>
            </div>

            {recentDocuments.length > 0 ? (
              <div className="space-y-2">
                {recentDocuments.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-900/30 border border-gray-700/30 rounded-lg group">
                    <div className="flex items-center gap-3">
                      <FiFileText className="w-4 h-4 text-blue-400" />
                      <div>
                        <div className="text-white font-mono text-sm truncate max-w-xs">
                          {doc.filename}
                        </div>
                        <div className="text-xs text-gray-500 font-mono">
                          {safeFormatDate(doc.created_at)}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => setSelectedDoc(doc)}
                      className="text-cyan-400 hover:text-cyan-300 transition-colors"
                    >
                      <FiEye className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className={tw.empty}>No recent documents</p>
            )}
          </div>
        </div>

        {/* Irrigation / Rachio */}
        {rachioZones && (
          <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-6 mb-6">
            <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
              <FiDroplet className="w-4 h-4 text-teal-400" />
              Irrigation
              <span className="ml-auto text-[10px] text-gray-600">{rachioZones.devices?.length} controller{rachioZones.devices?.length !== 1 ? 's' : ''}</span>
            </h2>
            <div className="space-y-4">
              {rachioZones.devices?.map((dev: any) => (
                <div key={dev.id}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${dev.online ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <span className="text-sm font-mono text-gray-300">{dev.name}</span>
                    {!dev.online && <span className="px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded text-[10px] font-bold">OFFLINE</span>}
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 ml-4">
                    {dev.zones?.filter((z: any) => z.last_watered_ms).map((zone: any) => (
                      <div key={zone.number} className="flex items-center justify-between text-[11px] font-mono bg-gray-900/50 rounded px-2 py-1.5">
                        <span className="text-gray-400 truncate mr-2">{zone.name}</span>
                        <span className={`flex-shrink-0 ${zone.hours_ago < 24 ? 'text-emerald-400' : zone.hours_ago < 72 ? 'text-teal-500' : 'text-gray-600'}`}>
                          {zone.hours_ago < 24 ? `${zone.hours_ago}h ago` : `${Math.round(zone.hours_ago / 24)}d ago`}
                        </span>
                      </div>
                    ))}
                    {dev.zones?.filter((z: any) => !z.last_watered_ms).map((zone: any) => (
                      <div key={zone.number} className="flex items-center justify-between text-[11px] font-mono bg-gray-900/30 rounded px-2 py-1.5 opacity-40">
                        <span className="text-gray-500 truncate">{zone.name}</span>
                        <span className="text-gray-700 flex-shrink-0">never</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Property Information */}
        <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-6 mb-6">
          <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-4 flex items-center gap-2">
            <FiInfo className="w-4 h-4 text-cyan-400" />
            Property Information
          </h2>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className={tw.statLabel}>Property Name:</div>
              <div className="text-white font-mono text-sm">{property.name || 'Not set'}</div>
            </div>
            <div>
              <div className={tw.statLabel}>Address:</div>
              <div className="text-white font-mono text-sm">{property.address || 'Not set'}</div>
            </div>
            <div>
              <div className={tw.statLabel}>Type:</div>
              <div className="text-white font-mono text-sm capitalize">{property.property_type || 'Not set'}</div>
            </div>
            <div>
              <div className={tw.statLabel}>Bedrooms/Bathrooms:</div>
              <div className="text-white font-mono text-sm">
                {property.bedrooms || 0} BR / {property.bathrooms || 0} BA
              </div>
            </div>
            <div>
              <div className={tw.statLabel}>Square Feet:</div>
              <div className="text-white font-mono text-sm">
                {property.square_feet ? `${property.square_feet.toLocaleString()} sq ft` : 'Not set'}
              </div>
            </div>
            <div>
              <div className={tw.statLabel}>Status:</div>
              <div className="text-white font-mono text-sm">
                <span className={`px-2 py-0.5 rounded text-xs font-mono border ${
                  property.occupancy_status === 'occupied' ? 'bg-green-500/15 text-green-400 border-green-500/30' :
                  property.occupancy_status === 'partially_occupied' ? 'bg-amber-500/15 text-amber-400 border-amber-500/30' :
                  'bg-gray-500/15 text-gray-400 border-gray-500/30'
                }`}>
                  {property.occupancy_status ? property.occupancy_status.replace('_', ' ') : 'vacant'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Current Tenants */}
        <CollapsibleSection
          title="Current Tenants"
          icon={<FiUsers className="w-5 h-5" />}
          defaultOpen={true}
          iconColor="text-green-500"
        >
          {property.tenant_name ? (
            <div className="overflow-x-auto mt-4">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700/50">
                    <th className="text-left py-3 text-xs font-mono text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="text-left py-3 text-xs font-mono text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="text-left py-3 text-xs font-mono text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="text-left py-3 text-xs font-mono text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="py-3 text-white font-mono text-sm">{property.tenant_name}</td>
                    <td className="py-3">
                      <a href={`mailto:${property.tenant_email}`} className="text-cyan-400 hover:text-cyan-300 font-mono text-sm">
                        {property.tenant_email}
                      </a>
                    </td>
                    <td className="py-3">
                      <a href={`tel:${property.tenant_phone}`} className="text-cyan-400 hover:text-cyan-300 font-mono text-sm">
                        {property.tenant_phone}
                      </a>
                    </td>
                    <td className="py-3">
                      <span className="px-2 py-0.5 bg-green-500/15 text-green-400 border border-green-500/30 rounded text-xs font-mono">
                        Current
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
              {property.lease_start_date && (
                <div className="mt-4 text-xs text-gray-500 font-mono">
                  Lease: {safeFormatDate(property.lease_start_date)} -
                  {property.lease_end_date ? safeFormatDate(property.lease_end_date) : 'Ongoing'}
                </div>
              )}
            </div>
          ) : (
            <p className={tw.empty + ' mt-4'}>No current tenants</p>
          )}
        </CollapsibleSection>

        {/* Inline Image Gallery */}
        {imageDocuments.length > 0 && (
          <div className="mt-6">
            <CollapsibleSection
              title="Property Images"
              icon={<FiImage className="w-5 h-5" />}
              defaultOpen={false}
              iconColor="text-purple-400"
            >
              <ImageGalleryInline documents={imageDocuments} />
            </CollapsibleSection>
          </div>
        )}

        {/* Financial Information */}
        <div className="mt-6">
          <CollapsibleSection
            title="Financial Information"
            icon={<FiDollarSign className="w-5 h-5" />}
            iconColor="text-green-400"
          >
            {hasFinancialInfo ? (
              <div className="grid grid-cols-2 gap-6 mt-4">
                <div>
                  <div className={tw.statLabel}>Year Purchased</div>
                  <div className="text-white font-mono text-sm">{property.year_purchased || 'Not set'}</div>
                </div>
                <div>
                  <div className={tw.statLabel}>Purchase Amount</div>
                  <div className="text-white font-mono text-sm">${property.purchase_amount?.toLocaleString() || '0'}</div>
                </div>
                <div>
                  <div className={tw.statLabel}>Current Value (Estimated)</div>
                  <div className="text-white font-mono text-sm">${property.current_value?.toLocaleString() || '0'}</div>
                </div>
                <div>
                  <div className={tw.statLabel}>Monthly Rent</div>
                  <div className="text-green-500 font-mono text-sm">${property.monthly_rent?.toLocaleString() || '0'}</div>
                </div>
                <div>
                  <div className={tw.statLabel}>Annual Property Taxes</div>
                  <div className="text-white font-mono text-sm">${property.annual_property_taxes?.toLocaleString() || '0'}</div>
                </div>
                <div>
                  <div className={tw.statLabel}>YTD Expenses</div>
                  <div className="text-white font-mono text-sm">${property.ytd_total?.toLocaleString() || '0'}</div>
                </div>
              </div>
            ) : (
              <p className={tw.empty + ' mt-4'}>No financial information available.</p>
            )}
          </CollapsibleSection>
        </div>

        {/* PocketSmith Cash Flow */}
        {cashFlow && cashFlow.transaction_count > 0 && (
          <div className="mt-6">
            <CollapsibleSection
              title={`Cash Flow (${cashFlow.transaction_count} transactions)`}
              icon={<FiBarChart2 className="w-5 h-5" />}
              iconColor="text-cyan-400"
            >
              <div className="mt-4">
                {/* Summary cards */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className={tw.statCard + ' text-center'}>
                    <div className={tw.statLabel}>Income (12mo)</div>
                    <div className="text-green-400 text-xl font-bold font-mono">
                      +${Math.abs(cashFlow.total_income).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className={tw.statCard + ' text-center'}>
                    <div className={tw.statLabel}>Expenses (12mo)</div>
                    <div className="text-red-400 text-xl font-bold font-mono">
                      -${Math.abs(cashFlow.total_expenses).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className={tw.statCard + ' text-center'}>
                    <div className={tw.statLabel}>Net</div>
                    <div className={`text-xl font-bold font-mono ${cashFlow.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      {cashFlow.net >= 0 ? '+' : '-'}${Math.abs(cashFlow.net).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>

                {/* Monthly bars */}
                {cashFlow.monthly_summary.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">Monthly Breakdown</h4>
                    <div className="space-y-2">
                      {cashFlow.monthly_summary.map((m, i) => {
                        const maxVal = Math.max(...cashFlow.monthly_summary.map(x => Math.max(x.income, Math.abs(x.expenses))));
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <div className="w-16 text-gray-500 text-xs font-mono flex-shrink-0">{m.month}</div>
                            <div className="flex-1">
                              <div className="flex gap-1">
                                <div className="h-3 rounded" style={{ width: `${maxVal > 0 ? (m.income / maxVal) * 100 : 0}%`, backgroundColor: '#34d399', minWidth: m.income > 0 ? '2px' : '0' }} />
                                <div className="h-3 rounded" style={{ width: `${maxVal > 0 ? (Math.abs(m.expenses) / maxVal) * 100 : 0}%`, backgroundColor: '#ef4444', minWidth: m.expenses < 0 ? '2px' : '0' }} />
                              </div>
                            </div>
                            <div className={`w-20 text-right text-xs font-mono flex-shrink-0 ${m.net >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              {m.net >= 0 ? '+' : ''}{m.net.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Recent transactions */}
                <h4 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-3">Recent Transactions</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {cashFlow.transactions.slice(0, 20).map(t => (
                    <div key={t.id} className="flex items-center justify-between bg-gray-900/30 border border-gray-700/30 rounded px-4 py-2">
                      <div className="flex items-center gap-3">
                        <span className="text-gray-500 text-xs font-mono w-20 flex-shrink-0">{t.date}</span>
                        <span className="text-white text-sm font-mono">{t.payee}</span>
                        <span className="text-gray-500 text-xs font-mono">{t.category_name}</span>
                      </div>
                      <span className={`font-mono text-sm font-medium ${t.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {t.amount >= 0 ? '+' : '-'}${Math.abs(t.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CollapsibleSection>
          </div>
        )}

        {/* HOA Information */}
        <div className="mt-6">
          <CollapsibleSection
            title="HOA Information"
            icon={<FiHome className="w-5 h-5" />}
            iconColor="text-cyan-400"
          >
            {hasHOAInfo ? (
              <div className="space-y-4 mt-4">
                {property.hoa_company_name && (
                  <div>
                    <div className={tw.statLabel}>HOA Company Name</div>
                    <div className="text-cyan-400 text-lg font-mono font-semibold">{property.hoa_company_name}</div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-6">
                  {property.hoa_account_number && (
                    <div>
                      <div className={tw.statLabel}>HOA Account Number</div>
                      <div className="text-white font-mono text-sm">{property.hoa_account_number}</div>
                    </div>
                  )}
                  {property.hoa_monthly_fee && (
                    <div>
                      <div className={tw.statLabel}>Monthly HOA Fee</div>
                      <div className="text-xl font-semibold text-green-400 font-mono">
                        ${property.hoa_monthly_fee.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  )}
                </div>
                {property.hoa_url && (
                  <div>
                    <div className={tw.statLabel}>HOA Payment Website</div>
                    <div>
                      <a
                        href={property.hoa_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-400 hover:text-cyan-300 font-mono text-sm flex items-center gap-2"
                      >
                        {property.hoa_url}
                        <FiExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                )}
                {property.hoa_login_url && (
                  <div>
                    <div className={tw.statLabel}>HOA Login Portal</div>
                    <div>
                      <a
                        href={property.hoa_login_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-cyan-400 hover:text-cyan-300 font-mono text-sm flex items-center gap-2"
                      >
                        {property.hoa_login_url}
                        <FiExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-6">
                  {property.hoa_username && (
                    <div>
                      <div className={tw.statLabel}>HOA Username</div>
                      <div className="text-white font-mono text-sm">{property.hoa_username}</div>
                    </div>
                  )}
                  {property.hoa_password && (
                    <div>
                      <div className={tw.statLabel}>HOA Password</div>
                      <div className="text-white font-mono text-sm">{property.hoa_password}</div>
                    </div>
                  )}
                </div>
                {property.hoa_notes && (
                  <div>
                    <div className={tw.statLabel}>HOA Notes</div>
                    <div className="text-white font-mono text-sm whitespace-pre-wrap">{property.hoa_notes}</div>
                  </div>
                )}

                {/* Payment Schedule Section */}
                {(property.payment_bank_account || property.payment_frequency || property.payment_schedule_start_date || property.next_payment_date || property.payment_details) && (
                  <div className="mt-6 pt-6 border-t border-gray-700/50">
                    <h4 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-4">Payment Schedule</h4>
                    <div className="grid grid-cols-2 gap-6">
                      {property.payment_bank_account && (
                        <div>
                          <div className={tw.statLabel}>Payment Bank/Account</div>
                          <div className="text-white font-mono text-sm">{property.payment_bank_account}</div>
                        </div>
                      )}
                      {property.payment_frequency && (
                        <div>
                          <div className={tw.statLabel}>Payment Frequency</div>
                          <div className="text-white font-mono text-sm">{property.payment_frequency}</div>
                        </div>
                      )}
                      {property.payment_schedule_start_date && (
                        <div>
                          <div className={tw.statLabel}>Schedule Start Date</div>
                          <div className="text-white font-mono text-sm">{safeFormatDate(property.payment_schedule_start_date)}</div>
                        </div>
                      )}
                      {property.next_payment_date && (
                        <div>
                          <div className={tw.statLabel}>Next Payment Date</div>
                          <div className="text-white font-mono text-sm">{safeFormatDate(property.next_payment_date)}</div>
                        </div>
                      )}
                    </div>
                    {property.payment_details && (
                      <div className="mt-4">
                        <div className={tw.statLabel}>Payment Details</div>
                        <div className="text-white font-mono text-sm whitespace-pre-wrap">{property.payment_details}</div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className={tw.empty + ' mt-4'}>No HOA information available.</p>
            )}
          </CollapsibleSection>
        </div>

        {/* Utility Accounts */}
        {hasUtilityInfo && (
        <div className="mt-6">
          <CollapsibleSection
            title="Utility Accounts"
            icon={<FiZap className="w-5 h-5" />}
            iconColor="text-yellow-400"
            defaultOpen={true}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              {property.cox_account && (
                <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-700/30">
                  <div className="flex justify-between items-start">
                    <div className={tw.statLabel}>Cox Communications</div>
                    {property.cox_monthly && <div className="text-green-400 font-mono font-semibold text-sm">${property.cox_monthly}/mo</div>}
                  </div>
                  <div className="text-white font-mono text-sm">{property.cox_account}</div>
                </div>
              )}
              {property.nv_energy_account && (
                <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-700/30">
                  <div className="flex justify-between items-start">
                    <div className={tw.statLabel}>NV Energy</div>
                    {property.nv_energy_monthly && <div className="text-green-400 font-mono font-semibold text-sm">${property.nv_energy_monthly}/mo</div>}
                  </div>
                  <div className="text-white font-mono text-sm">{property.nv_energy_account}</div>
                </div>
              )}
              {property.sw_gas_account && (
                <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-700/30">
                  <div className="flex justify-between items-start">
                    <div className={tw.statLabel}>Southwest Gas</div>
                    {property.sw_gas_monthly && <div className="text-green-400 font-mono font-semibold text-sm">${property.sw_gas_monthly}/mo</div>}
                  </div>
                  <div className="text-white font-mono text-sm">{property.sw_gas_account}</div>
                </div>
              )}
              {property.water_account && (
                <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-700/30">
                  <div className="flex justify-between items-start">
                    <div className={tw.statLabel}>LV Valley Water</div>
                    {property.water_monthly && <div className="text-green-400 font-mono font-semibold text-sm">${property.water_monthly}/mo</div>}
                  </div>
                  <div className="text-white font-mono text-sm">{property.water_account}</div>
                </div>
              )}
              {property.republic_account && (
                <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-700/30">
                  <div className="flex justify-between items-start">
                    <div className={tw.statLabel}>Republic Services (Trash)</div>
                  </div>
                  <div className="text-white font-mono text-sm">{property.republic_account}</div>
                </div>
              )}
            </div>
          </CollapsibleSection>
        </div>
        )}

        {/* Rent Payment History */}
        {rentPayments && rentPayments.data.length > 0 && (
        <div className="mt-6">
          <CollapsibleSection
            title={`Rent Payments (${rentPayments.count} payments)`}
            icon={<FiDollarSign className="w-5 h-5" />}
            iconColor="text-green-400"
            defaultOpen={true}
          >
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4 mt-4 mb-4">
              <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700/30 text-center">
                <div className={tw.statLabel}>Total Collected</div>
                <div className="text-green-400 font-mono font-bold text-lg">${rentPayments.total.toLocaleString()}</div>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700/30 text-center">
                <div className={tw.statLabel}>Avg Monthly</div>
                <div className="text-cyan-400 font-mono font-bold text-lg">${rentPayments.avg_monthly.toLocaleString()}</div>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700/30 text-center">
                <div className={tw.statLabel}>Expected Rent</div>
                <div className="text-yellow-400 font-mono font-bold text-lg">{property?.monthly_rent ? `$${Number(property.monthly_rent).toLocaleString()}` : 'N/A'}</div>
              </div>
            </div>

            {/* Payment Timeline */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left text-xs text-gray-500 font-mono uppercase px-3 py-2">Date Paid</th>
                    <th className="text-left text-xs text-gray-500 font-mono uppercase px-3 py-2">Tenant</th>
                    <th className="text-right text-xs text-gray-500 font-mono uppercase px-3 py-2">Amount</th>
                    <th className="text-left text-xs text-gray-500 font-mono uppercase px-3 py-2">Source</th>
                    <th className="text-center text-xs text-gray-500 font-mono uppercase px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rentPayments.data.map((pmt) => {
                    const paidDate = new Date(pmt.date + 'T00:00:00');
                    const dayOfMonth = paidDate.getDate();
                    const isLate = dayOfMonth > 5;
                    const isOnTime = dayOfMonth <= 5;
                    return (
                      <tr key={pmt.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                        <td className="px-3 py-2 text-sm font-mono text-gray-300">
                          {paidDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td className="px-3 py-2 text-sm font-mono text-white">
                          {pmt.payee.replace(/^SIN CITY RENTALS\s*/i, '').replace(/^Zelle.*?From\s*/i, '').replace(/\s*Id Nbr:.*/i, '').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}
                        </td>
                        <td className="px-3 py-2 text-sm font-mono text-right font-semibold text-green-400">
                          ${pmt.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2 text-xs font-mono text-gray-500">
                          {pmt.account === 'Novo Checking' ? 'TenantCloud' : pmt.account.includes('5958') ? 'Zelle' : pmt.account}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <span className={`text-xs font-mono px-2 py-0.5 rounded ${isOnTime ? 'bg-green-900/30 text-green-400' : 'bg-yellow-900/30 text-yellow-400'}`}>
                            {isOnTime ? 'On Time' : `Day ${dayOfMonth}`}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
        </div>
        )}

        {/* Utility Bill History */}
        {utilityBills && utilityBills.data.length > 0 && (
        <div className="mt-6">
          <CollapsibleSection
            title={`Utility Bill History (${utilityBills.data.length} bills)`}
            icon={<FiBarChart2 className="w-5 h-5" />}
            iconColor="text-cyan-400"
            defaultOpen={true}
          >
            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-4 mt-4 mb-4">
              <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700/30 text-center">
                <div className={tw.statLabel}>12-Month Total</div>
                <div className="text-green-400 font-mono font-bold text-lg">${utilityBills.total_12mo.toLocaleString()}</div>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700/30 text-center">
                <div className={tw.statLabel}>Avg Monthly</div>
                <div className="text-cyan-400 font-mono font-bold text-lg">${utilityBills.avg_monthly.toFixed(2)}</div>
              </div>
              <div className="bg-gray-900/50 rounded-lg p-3 border border-gray-700/30 text-center">
                <div className={tw.statLabel}>Latest Bill</div>
                <div className="text-yellow-400 font-mono font-bold text-lg">${utilityBills.data[0].amount.toFixed(2)}</div>
              </div>
            </div>

            {/* Usage Bar Chart */}
            <div className="bg-gray-900/30 rounded-lg p-4 border border-gray-700/30 mb-4">
              <div className={tw.statLabel + ' mb-3'}>Monthly Usage & Cost</div>
              <div className="flex items-end gap-1 h-32">
                {[...utilityBills.data].reverse().map((bill) => {
                  const maxAmount = Math.max(...utilityBills.data.map(b => b.amount));
                  const pct = (bill.amount / maxAmount) * 100;
                  const month = new Date(bill.billing_date).toLocaleDateString('en-US', { month: 'short' });
                  return (
                    <div key={bill.id} className="flex-1 flex flex-col items-center gap-1" title={`${month}: $${bill.amount} / ${bill.usage_amount}${bill.usage_unit || ''}`}>
                      <span className="text-[10px] text-gray-400 font-mono">${bill.amount.toFixed(0)}</span>
                      <div
                        className="w-full rounded-t transition-all"
                        style={{
                          height: `${pct}%`,
                          backgroundColor: bill.amount > 100 ? '#ef4444' : bill.amount > 85 ? '#f59e0b' : '#22d3ee',
                          minHeight: '4px'
                        }}
                      />
                      <span className="text-[9px] text-gray-500 font-mono">{month}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bills Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left text-xs text-gray-500 font-mono uppercase px-3 py-2">Date</th>
                    <th className="text-left text-xs text-gray-500 font-mono uppercase px-3 py-2">Provider</th>
                    <th className="text-right text-xs text-gray-500 font-mono uppercase px-3 py-2">Amount</th>
                    <th className="text-right text-xs text-gray-500 font-mono uppercase px-3 py-2">Usage</th>
                    <th className="text-left text-xs text-gray-500 font-mono uppercase px-3 py-2">Notes</th>
                    <th className="text-center text-xs text-gray-500 font-mono uppercase px-3 py-2">PDF</th>
                  </tr>
                </thead>
                <tbody>
                  {utilityBills.data.map((bill) => (
                    <tr key={bill.id} className="border-b border-gray-800/50 hover:bg-gray-800/30">
                      <td className="px-3 py-2 text-sm font-mono text-gray-300">
                        {new Date(bill.billing_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                      </td>
                      <td className="px-3 py-2 text-sm font-mono text-white">{bill.provider}</td>
                      <td className={`px-3 py-2 text-sm font-mono text-right font-semibold ${bill.amount > 100 ? 'text-red-400' : 'text-green-400'}`}>
                        ${bill.amount.toFixed(2)}
                      </td>
                      <td className="px-3 py-2 text-sm font-mono text-cyan-400 text-right">
                        {bill.usage_amount}{bill.usage_unit ? ` ${bill.usage_unit}` : ''}
                      </td>
                      <td className="px-3 py-2 text-xs font-mono text-gray-500 max-w-xs truncate">{bill.notes || ''}</td>
                      <td className="px-3 py-2 text-center">
                        {bill.pdf_filename && (
                          <a
                            href={`/uploads/utility_bills/${bill.pdf_filename}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cyan-400 hover:text-cyan-300"
                            title="View PDF"
                          >
                            <FiFileText className="w-4 h-4 inline" />
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CollapsibleSection>
        </div>
        )}

        {/* Access Information */}
        <div className="mt-6">
          <CollapsibleSection
            title="Access Information"
            icon={<FiKey className="w-5 h-5" />}
            iconColor="text-yellow-400"
          >
            {hasAccessInfo ? (
              <div className="grid grid-cols-2 gap-6 mt-4">
                <div>
                  <div className={tw.statLabel}>Lockbox Code</div>
                  <div className="text-white font-mono text-sm">{property.lockbox_code || 'Not set'}</div>
                </div>
                <div>
                  <div className={tw.statLabel}>Garage Code</div>
                  <div className="text-white font-mono text-sm">{property.garage_code || 'Not set'}</div>
                </div>
                <div>
                  <div className={tw.statLabel}>WiFi Network</div>
                  <div className="text-white font-mono text-sm">{property.wifi_network || 'Not set'}</div>
                </div>
                <div>
                  <div className={tw.statLabel}>WiFi Password</div>
                  <div className="text-white font-mono text-sm">{property.wifi_password || 'Not set'}</div>
                </div>
              </div>
            ) : (
              <p className={tw.empty + ' mt-4'}>No access information available.</p>
            )}
          </CollapsibleSection>
        </div>

        {/* Recent Messages */}
        <div className="mt-6">
          <CollapsibleSection
            title="Recent Communications"
            icon={<FiMessageSquare className="w-5 h-5" />}
            iconColor="text-cyan-400"
          >
            <div className="mt-4">
              <p className={tw.empty}>No recent messages found</p>
              <div className="mt-4 text-center">
                <button className={tw.btnPrimary}>
                  View All Messages for This Property
                </button>
              </div>
            </div>
          </CollapsibleSection>
        </div>
      {/* Document Viewer Modal */}
      {selectedDoc && (
        <DocumentViewer
          document={selectedDoc}
          onClose={() => setSelectedDoc(null)}
        />
      )}
      </div>
    </div>
  );
};

export default PropertyDetails;
